import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Calendar, Clock, MapPin, MessageSquare, LogOut, LogIn, Eye, Search, AlertTriangle, CheckCircle } from 'lucide-react';
import OutingNavigation from './OutingNavigation';

const SecurityOutingPortal = () => {
  const { user } = useAuth();
  const [activeOutings, setActiveOutings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedOuting, setSelectedOuting] = useState(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Exit/Entry form state
  const [exitEntryData, setExitEntryData] = useState({
    message: '',
    specialInstructions: ''
  });

  useEffect(() => {
    if (user) {
      fetchActiveOutings();
    }
  }, [user]);

  const fetchActiveOutings = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/outing/security/active-outings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActiveOutings(data.activeOutings || []);
      }
    } catch (error) {
      console.error('Error fetching active outings:', error);
      setError('Failed to fetch active outings');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentExit = async () => {
    if (!selectedOuting) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/outing/request/${selectedOuting._id}/exit`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(exitEntryData)
      });

      if (response.ok) {
        const result = await response.json();
        // Update the outing in the list
        setActiveOutings(prev => 
          prev.map(outing => 
            outing._id === selectedOuting._id ? result.outingRequest : outing
          )
        );
        setShowExitModal(false);
        setSelectedOuting(null);
        setExitEntryData({ message: '', specialInstructions: '' });
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to record student exit');
      }
    } catch (error) {
      console.error('Error recording student exit:', error);
      setError('Failed to record student exit');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentEntry = async () => {
    if (!selectedOuting) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/outing/request/${selectedOuting._id}/entry`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(exitEntryData)
      });

      if (response.ok) {
        const result = await response.json();
        // Remove the outing from active list since it's completed
        setActiveOutings(prev => 
          prev.filter(outing => outing._id !== selectedOuting._id)
        );
        setShowEntryModal(false);
        setSelectedOuting(null);
        setExitEntryData({ message: '', specialInstructions: '' });
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to record student entry');
      }
    } catch (error) {
      console.error('Error recording student entry:', error);
      setError('Failed to record student entry');
    } finally {
      setLoading(false);
    }
  };

  const openExitModal = (outing) => {
    setSelectedOuting(outing);
    setExitEntryData({ message: '', specialInstructions: '' });
    setShowExitModal(true);
  };

  const openEntryModal = (outing) => {
    setSelectedOuting(outing);
    setExitEntryData({ message: '', specialInstructions: '' });
    setShowEntryModal(true);
  };

  const canStudentExit = (outing) => {
    return outing.approvalStatus === 'approved' && !outing.security?.isCurrentlyOut;
  };

  const canStudentEnter = (outing) => {
    return outing.approvalStatus === 'approved' && outing.security?.isCurrentlyOut;
  };

  const getFilteredOutings = () => {
    if (!searchQuery.trim()) return activeOutings;
    
    return activeOutings.filter(outing => 
      outing.studentDetails?.studentId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      outing.studentDetails?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      outing.studentDetails?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      outing.requestId?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'denied': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'denied': return 'Denied';
      default: return 'Unknown';
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const filteredOutings = getFilteredOutings();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <OutingNavigation userRole="security" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Message */}
        <div className="mb-6">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Calendar className="h-6 w-6 text-orange-600" />
              <div>
                <h3 className="text-lg font-medium text-orange-900">
                  Welcome, {user.profile?.firstName || 'Security'} ({user.staffDetails?.designation || 'Security'})!
                </h3>
                <p className="text-orange-700">
                  Monitor student exit/entry and track active outings here.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Page Header */}
        <div className="mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Security Monitoring</h2>
            <p className="text-gray-600">Track student exit/entry and monitor active outings</p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by student ID, name, or request ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600">
              {filteredOutings.length} active outing{filteredOutings.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Active Outings List */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Active Outings</h3>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading active outings...</p>
            </div>
          ) : filteredOutings.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                {searchQuery ? 'No outings match your search' : 'No active outings found'}
              </p>
              <p className="text-sm text-gray-500">
                {searchQuery ? 'Try adjusting your search terms' : 'All students are currently on campus'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredOutings.map((outing) => (
                <div key={outing._id} className="p-6 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(outing.approvalStatus)}`}>
                          {getStatusText(outing.approvalStatus)}
                        </span>
                        <span className="text-sm text-gray-500">
                          Request ID: {outing.requestId}
                        </span>
                        {outing.security?.isCurrentlyOut ? (
                          <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full flex items-center">
                            <LogOut className="h-3 w-3 mr-1" />
                            Student Out
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Student On Campus
                          </span>
                        )}
                      </div>

                      {/* Student Information */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <h4 className="text-sm font-medium text-blue-800 mb-2">Student Details:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-blue-700 font-medium">Name:</span>
                            <span className="text-blue-600 ml-2">
                              {outing.studentDetails?.firstName} {outing.studentDetails?.lastName}
                            </span>
                          </div>
                          <div>
                            <span className="text-blue-700 font-medium">Student ID:</span>
                            <span className="text-blue-600 ml-2">
                              {outing.studentDetails?.studentId}
                            </span>
                          </div>
                          <div>
                            <span className="text-blue-700 font-medium">Grade:</span>
                            <span className="text-blue-600 ml-2">
                              {outing.studentDetails?.grade}
                            </span>
                          </div>
                          <div>
                            <span className="text-blue-700 font-medium">Campus:</span>
                            <span className="text-blue-600 ml-2">
                              {outing.campus}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Outing Details */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {new Date(outing.outingDate).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {outing.departureTime} - {outing.returnTime}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {outing.numberOfDays} day{outing.numberOfDays > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>

                      {/* Parent Permission Details */}
                      {outing.parentPermission?.isGranted && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-3">
                          <h4 className="text-sm font-medium text-green-800 mb-2">Parent Permission:</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-green-700 font-medium">Pickup:</span>
                              <span className="text-green-600 ml-2">
                                {new Date(outing.parentPermission.pickupDate).toLocaleDateString()} at {outing.parentPermission.pickupTime}
                              </span>
                            </div>
                            <div>
                              <span className="text-green-700 font-medium">Drop:</span>
                              <span className="text-green-600 ml-2">
                                {new Date(outing.parentPermission.dropDate).toLocaleDateString()} at {outing.parentPermission.dropTime}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Security Tracking */}
                      {outing.security && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Security Tracking:</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            {outing.security.exitTime && (
                              <div>
                                <span className="text-gray-600 font-medium">Exit Time:</span>
                                <span className="text-gray-500 ml-2">
                                  {new Date(outing.security.exitTime).toLocaleString()}
                                </span>
                              </div>
                            )}
                            {outing.security.entryTime && (
                              <div>
                                <span className="text-gray-600 font-medium">Entry Time:</span>
                                <span className="text-gray-500 ml-2">
                                  {new Date(outing.security.entryTime).toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      {canStudentExit(outing) && (
                        <button
                          onClick={() => openExitModal(outing)}
                          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm flex items-center"
                        >
                          <LogOut className="h-4 w-4 mr-2" />
                          Record Exit
                        </button>
                      )}
                      {canStudentEnter(outing) && (
                        <button
                          onClick={() => openEntryModal(outing)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center"
                        >
                          <LogIn className="h-4 w-4 mr-2" />
                          Record Entry
                        </button>
                      )}
                      <button
                        onClick={() => {/* TODO: View details modal */}}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Exit Modal */}
      {showExitModal && selectedOuting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Record Student Exit</h3>
              <p className="text-sm text-gray-600">Confirm student is leaving campus</p>
            </div>

            <div className="px-6 py-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={exitEntryData.message}
                  onChange={(e) => setExitEntryData({...exitEntryData, message: e.target.value})}
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Any additional notes about the exit..."
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Instructions (Optional)
                </label>
                <textarea
                  value={exitEntryData.specialInstructions}
                  onChange={(e) => setExitEntryData({...exitEntryData, specialInstructions: e.target.value})}
                  rows="2"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Any special instructions for the student..."
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowExitModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleStudentExit}
                  disabled={loading}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Recording...
                    </>
                  ) : (
                    <>
                      <LogOut className="h-4 w-4 mr-2" />
                      Record Exit
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Entry Modal */}
      {showEntryModal && selectedOuting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Record Student Entry</h3>
              <p className="text-sm text-gray-600">Confirm student has returned to campus</p>
            </div>

            <div className="px-6 py-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Return Notes (Optional)
                </label>
                <textarea
                  value={exitEntryData.message}
                  onChange={(e) => setExitEntryData({...exitEntryData, message: e.target.value})}
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Any notes about the student's return..."
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Instructions (Optional)
                </label>
                <textarea
                  value={exitEntryData.specialInstructions}
                  onChange={(e) => setExitEntryData({...exitEntryData, specialInstructions: e.target.value})}
                  rows="2"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Any special instructions for the student..."
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEntryModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleStudentEntry}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Recording...
                    </>
                  ) : (
                    <>
                      <LogIn className="h-4 w-4 mr-2" />
                      Record Entry
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityOutingPortal;
